import React, { useEffect, useState, useCallback } from 'react';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { checklistTemplateAPI, venueAPI } from '../lib/api';
import { v4 as uuid } from 'uuid';
import { 
  Plus, Upload, Download, Copy, Trash2, Lock, Unlock, FileSpreadsheet,
  ClipboardCheck, Loader2, AlertCircle, X, Check, ChevronDown, ChevronUp,
  Edit2, Save, Building2, FolderOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const ChecklistBuilderPage = () => {
  const { user } = useAuthStore();
  const [templates, setTemplates] = useState([]);
  const [masterTemplates, setMasterTemplates] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const userPermissions = user?.custom_permissions || [];
  const isAdminOrOps = user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.OPS;
  const canCreate = isAdminOrOps || userPermissions.includes('can_create_checklists');
  const canEditLocked = isAdminOrOps || userPermissions.includes('can_edit_locked_items');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use catch on individual promises to prevent full page crash if one API fails
      const [templatesRes, mastersRes, venuesRes] = await Promise.all([
        checklistTemplateAPI.list(null, false, true).catch(() => ({ data: [] })),
        checklistTemplateAPI.getMasterTemplates().catch(() => ({ data: [] })),
        venueAPI.list().catch(() => ({ data: [] })),
      ]);
      setTemplates(templatesRes.data?.filter(t => !t.is_template) || []);
      setMasterTemplates(mastersRes.data || []);
      setVenues(venuesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load some builder templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (!canCreate && !isAdminOrOps) {
    return (
      <div className="card-rtb text-center py-12">
        <Lock className="w-16 h-16 text-[#3F3F46] mx-auto mb-4" />
        <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">Access Restricted</h3>
        <p className="text-[#A1A1AA]">You don't have permission to access the Checklist Builder.</p>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#FF6600]" /></div>;

  // If editing a template, show the editor view
  if (editingTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white uppercase">
            {editingTemplate.id ? 'Edit Template' : 'Create Template'}
          </h1>
          <Button 
            onClick={() => setEditingTemplate(null)} 
            className="btn-rtb-secondary"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>

        <div className="card-rtb space-y-4">
          <div>
            <Label className="text-white font-bold mb-2 block">Template Name</Label>
            <Input 
              value={editingTemplate.name || ''} 
              onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
              placeholder="e.g., Opening Checklist"
              className="input-rtb"
            />
          </div>

          <div>
            <Label className="text-white font-bold mb-2 block">Type</Label>
            <Select value={editingTemplate.checklist_type || 'opening'} onValueChange={(val) => setEditingTemplate({...editingTemplate, checklist_type: val})}>
              <SelectTrigger className="input-rtb">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#27272A] border-[#3F3F46]">
                <SelectItem value="opening">Opening</SelectItem>
                <SelectItem value="closing">Closing</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-white font-bold mb-2 block">Venue</Label>
            <Select value={editingTemplate.venue_id || ''} onValueChange={(val) => setEditingTemplate({...editingTemplate, venue_id: val})}>
              <SelectTrigger className="input-rtb">
                <SelectValue placeholder="Select venue" />
              </SelectTrigger>
              <SelectContent className="bg-[#27272A] border-[#3F3F46]">
                {venues.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white font-bold mb-2 block">Unlock Hour</Label>
              <Input 
                type="number" 
                min="0" 
                max="23"
                value={editingTemplate.unlock_hour || 0} 
                onChange={(e) => setEditingTemplate({...editingTemplate, unlock_hour: parseInt(e.target.value)})}
                className="input-rtb"
              />
            </div>
            <div>
              <Label className="text-white font-bold mb-2 block">Lock Hour</Label>
              <Input 
                type="number" 
                min="0" 
                max="23"
                value={editingTemplate.lock_hour || 23} 
                onChange={(e) => setEditingTemplate({...editingTemplate, lock_hour: parseInt(e.target.value)})}
                className="input-rtb"
              />
            </div>
          </div>
        </div>

        <div className="card-rtb space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Items</h3>
            <Button 
              onClick={() => {
                const newItem = {id: uuid(), label: '', item_type: 'yes_no', required: true};
                setEditingTemplate({
                  ...editingTemplate, 
                  items: [...(editingTemplate.items || []), newItem]
                });
              }}
              className="btn-rtb-secondary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>

          {(editingTemplate.items || []).map((item, idx) => (
            <div key={item.id} className="p-4 border border-[#3F3F46] rounded-md space-y-3">
              <div className="flex items-center justify-between">
                <Input 
                  value={item.label || ''} 
                  onChange={(e) => {
                    const items = [...editingTemplate.items];
                    items[idx].label = e.target.value;
                    setEditingTemplate({...editingTemplate, items});
                  }}
                  placeholder="Item label"
                  className="input-rtb flex-1 mr-2"
                />
                <Button 
                  onClick={() => {
                    const items = editingTemplate.items.filter((_, i) => i !== idx);
                    setEditingTemplate({...editingTemplate, items});
                  }}
                  className="btn-rtb-secondary"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <Select value={item.item_type || 'yes_no'} onValueChange={(val) => {
                const items = [...editingTemplate.items];
                items[idx].item_type = val;
                setEditingTemplate({...editingTemplate, items});
              }}>
                <SelectTrigger className="input-rtb">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27272A] border-[#3F3F46]">
                  <SelectItem value="yes_no">Yes/No</SelectItem>
                  <SelectItem value="temperature">Temperature</SelectItem>
                  <SelectItem value="photo">Photo</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        <Button 
          onClick={async () => {
            setSaving(true);
            try {
              // Validate required fields before sending
              if (!editingTemplate.name?.trim()) {
                toast.error('Template name is required');
                setSaving(false);
                return;
              }
              if (!editingTemplate.checklist_type) {
                toast.error('Checklist type is required');
                setSaving(false);
                return;
              }
              if (!editingTemplate.items || editingTemplate.items.length === 0) {
                toast.error('Template must have at least one item');
                setSaving(false);
                return;
              }

              if (editingTemplate.id) {
                await checklistTemplateAPI.update(editingTemplate.id, editingTemplate);
                toast.success('Template updated');
              } else {
                await checklistTemplateAPI.create(editingTemplate);
                toast.success('Template created');
              }
              setEditingTemplate(null);
              fetchData();
            } catch (error) {
              // FIXED: Safely extract error message (handle both string and object responses)
              let errorMessage = 'Failed to save template';
              
              if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                // If detail is an object (Pydantic validation error), convert to string
                errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
              } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
              } else if (error.message) {
                errorMessage = error.message;
              }
              
              // Truncate long error messages
              if (errorMessage.length > 200) {
                errorMessage = errorMessage.substring(0, 200) + '...';
              }
              
              toast.error(errorMessage);
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
          className="btn-rtb-primary w-full"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Template
        </Button>
      </div>
    );
  }

  // Main view - list templates
  return (
    <div className="space-y-6 form-mobile-safe">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white uppercase">Checklist Builder</h1>
        <Button 
          onClick={() => {
            // Initialize with all required fields for backend validation
            setEditingTemplate({
              name: '',
              checklist_type: 'opening',
              items: [],
              unlock_hour: 6,
              lock_hour: 23,
              is_template: false,
              description: '',
            });
          }}
          className="btn-rtb-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      <Tabs defaultValue="templates">
        <TabsList className="bg-[#1E1E1E] border border-[#3F3F46]">
          <TabsTrigger value="templates">My Templates</TabsTrigger>
          <TabsTrigger value="masters">Master Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          {templates.length > 0 ? (
            templates.map(template => (
              <div key={template.id} className="card-rtb p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold">{template.name}</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    {venues.find(v => v.id === template.venue_id)?.name || 'Master Template'} • {template.items?.length || 0} items
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setEditingTemplate(template)}
                    className="btn-rtb-secondary"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button 
                    onClick={async () => {
                      if (confirm('Delete this template?')) {
                        try {
                          await checklistTemplateAPI.delete(template.id);
                          toast.success('Template deleted');
                          fetchData();
                        } catch (error) {
                          toast.error('Failed to delete template');
                        }
                      }
                    }}
                    className="btn-rtb-secondary"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="card-rtb text-center py-12">
              <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No templates created yet. Create your first one!</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="masters" className="space-y-4">
          {masterTemplates.length > 0 ? (
            masterTemplates.map(template => (
              <div key={template.id} className="card-rtb p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold">{template.name}</h3>
                  <p className="text-[#A1A1AA] text-sm">{template.items?.length || 0} items</p>
                </div>
                <Button 
                  onClick={() => setCloneTarget(template)}
                  className="btn-rtb-primary"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Clone to Venue
                </Button>
              </div>
            ))
          ) : (
            <div className="card-rtb text-center py-12">
              <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No master templates available.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {cloneTarget && (
        <Dialog open={!!cloneTarget} onOpenChange={() => setCloneTarget(null)}>
          <DialogContent className="bg-[#1E1E1E] border-[#3F3F46]">
            <DialogHeader>
              <DialogTitle className="text-white">Clone Template to Venue</DialogTitle>
            </DialogHeader>
            <Select onValueChange={async (venueId) => {
              try {
                await checklistTemplateAPI.clone(cloneTarget.id, venueId);
                toast.success('Template cloned');
                setCloneTarget(null);
                fetchData();
              } catch (error) {
                toast.error('Failed to clone template');
              }
            }}>
              <SelectTrigger className="input-rtb">
                <SelectValue placeholder="Select venue" />
              </SelectTrigger>
              <SelectContent className="bg-[#27272A] border-[#3F3F46]">
                {venues.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ChecklistBuilderPage;